/**
 * Service Instructions Page
 *
 * Admin CRUD + user read-only view for service instructions
 * that explain each service the platform provides.
 * AI agents can also read these via tools.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Stack,
  InputAdornment,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Tooltip,
  Collapse,
  Container,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  MenuBook as MenuBookIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { serviceInstructionsApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import type {
  ServiceInstruction,
  ServiceInstructionCreate,
  ServiceInstructionListItem,
  ServiceInstructionCategory,
} from '../../types';

const CATEGORIES: { value: ServiceInstructionCategory; label: string }[] = [
  { value: 'trading', label: 'Trading' },
  { value: 'ai', label: 'AI & Automation' },
  { value: 'analysis', label: 'Analysis' },
  { value: 'notifications', label: 'Notifications & Alerts' },
  { value: 'connectors', label: 'Connectors & Integrations' },
  { value: 'risk', label: 'Risk Management' },
  { value: 'billing', label: 'Billing & Subscriptions' },
  { value: 'general', label: 'General' },
];

const categoryColor: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  trading: 'primary',
  ai: 'secondary',
  analysis: 'info',
  notifications: 'warning',
  connectors: 'success',
  risk: 'error',
  billing: 'default',
  general: 'default',
};

interface FormData {
  title: string;
  service_key: string;
  category: ServiceInstructionCategory;
  summary: string;
  content: string;
  is_active: boolean;
  priority: number;
  tags: string[];
}

const emptyForm: FormData = {
  title: '',
  service_key: '',
  category: 'general',
  summary: '',
  content: '',
  is_active: true,
  priority: 0,
  tags: [],
};

const ServiceInstructionsPage: React.FC = () => {
  const queryClient = useQueryClient();

  // Filters
  const [filterCategory, setFilterCategory] = useState<ServiceInstructionCategory | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [tagInput, setTagInput] = useState('');

  // Expanded row for inline preview
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Delete confirm
  const [deleteSlug, setDeleteSlug] = useState<string | null>(null);

  // Check if user is admin (staff) via AuthContext
  const { user } = useAuth();
  const isAdmin = user?.is_staff === true;

  // ---- Queries ----
  const { data: instructions = [], isLoading, error } = useQuery<ServiceInstructionListItem[]>({
    queryKey: ['service-instructions', filterCategory, searchQuery],
    queryFn: () =>
      serviceInstructionsApi.list({
        ...(filterCategory !== 'all' ? { category: filterCategory } : {}),
        ...(searchQuery ? { search: searchQuery } : {}),
      }),
  });

  // ---- Mutations ----
  const createMutation = useMutation({
    mutationFn: (data: ServiceInstructionCreate) => serviceInstructionsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-instructions'] });
      toast.success('Service instruction created');
      closeDialog();
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || 'Failed to create'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ slug, data }: { slug: string; data: Partial<ServiceInstructionCreate> }) =>
      serviceInstructionsApi.update(slug, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-instructions'] });
      toast.success('Service instruction updated');
      closeDialog();
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || 'Failed to update'),
  });

  const deleteMutation = useMutation({
    mutationFn: (slug: string) => serviceInstructionsApi.delete(slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-instructions'] });
      toast.success('Service instruction deleted');
      setDeleteSlug(null);
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || 'Failed to delete'),
  });

  // ---- Handlers ----
  const closeDialog = () => {
    setDialogOpen(false);
    setEditingSlug(null);
    setFormData(emptyForm);
    setTagInput('');
  };

  const openCreate = () => {
    setFormData(emptyForm);
    setEditingSlug(null);
    setDialogOpen(true);
  };

  const openEdit = async (item: ServiceInstructionListItem) => {
    try {
      const full = await serviceInstructionsApi.get(item.slug);
      setFormData({
        title: full.title,
        service_key: full.service_key,
        category: full.category,
        summary: full.summary || '',
        content: full.content,
        is_active: full.is_active,
        priority: full.priority,
        tags: full.tags || [],
      });
      setEditingSlug(full.slug);
      setDialogOpen(true);
    } catch {
      toast.error('Failed to load instruction details');
    }
  };

  const handleSubmit = () => {
    const payload: ServiceInstructionCreate = {
      title: formData.title,
      service_key: formData.service_key,
      category: formData.category,
      summary: formData.summary,
      content: formData.content,
      is_active: formData.is_active,
      priority: formData.priority,
      tags: formData.tags,
    };
    if (editingSlug) {
      updateMutation.mutate({ slug: editingSlug, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !formData.tags.includes(t)) {
      setFormData((prev) => ({ ...prev, tags: [...prev.tags, t] }));
    }
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    setFormData((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
  };

  const toggleExpand = (id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const copyServiceKey = (key: string) => {
    navigator.clipboard.writeText(key);
    toast.info(`Copied: ${key}`);
  };

  // ---- Render ----
  return (
    <Container maxWidth={false} disableGutters>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <MenuBookIcon color="primary" />
          <Typography variant="h5" fontWeight="bold">
            Service Instructions
          </Typography>
        </Box>
        {isAdmin && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New Instruction
          </Button>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" mb={2}>
        Instructions explaining each service the platform provides. AI agents use these to guide users.
      </Typography>

      {/* Filters */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
        <TextField
          size="small"
          placeholder="Search instructions…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={filterCategory}
            label="Category"
            onChange={(e) => setFilterCategory(e.target.value as any)}
          >
            <MenuItem value="all">All Categories</MenuItem>
            {CATEGORIES.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Error / Loading */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load instructions: {(error as any)?.message || 'Unknown error'}
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : instructions.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography color="text.secondary" textAlign="center">
              {searchQuery || filterCategory !== 'all'
                ? 'No instructions match your filters.'
                : 'No service instructions yet. Click "New Instruction" to create one.'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={40} />
                <TableCell>Title</TableCell>
                <TableCell>Service Key</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Summary</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="center">Priority</TableCell>
                {isAdmin && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {instructions.map((item) => (
                <React.Fragment key={item.id}>
                  <TableRow
                    hover
                    sx={{ cursor: 'pointer', '& > *': { borderBottom: expandedId === item.id ? 'none' : undefined } }}
                    onClick={() => toggleExpand(item.id)}
                  >
                    <TableCell>
                      <IconButton size="small">
                        {expandedId === item.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {item.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Click to copy">
                        <Chip
                          label={item.service_key}
                          size="small"
                          variant="outlined"
                          icon={<CopyIcon fontSize="small" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            copyServiceKey(item.service_key);
                          }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={CATEGORIES.find((c) => c.value === item.category)?.label || item.category}
                        size="small"
                        color={categoryColor[item.category] || 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 300 }}>
                        {item.summary || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={item.is_active ? 'Yes' : 'No'}
                        size="small"
                        color={item.is_active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">{item.priority}</TableCell>
                    {isAdmin && (
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(item);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteSlug(item.slug);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                  {/* Expanded detail row */}
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 8 : 7} sx={{ py: 0, px: 2 }}>
                      <Collapse in={expandedId === item.id} timeout="auto" unmountOnExit>
                        <ExpandedContent slug={item.slug} tags={item.tags} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSlug ? 'Edit Service Instruction' : 'New Service Instruction'}
          <IconButton onClick={closeDialog} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Title"
              required
              fullWidth
              value={formData.title}
              onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
            />
            <TextField
              label="Service Key"
              required
              fullWidth
              helperText="Unique machine-readable key (e.g. expert_builder). Agents use this."
              value={formData.service_key}
              onChange={(e) => setFormData((p) => ({ ...p, service_key: e.target.value }))}
            />
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) => setFormData((p) => ({ ...p, category: e.target.value as ServiceInstructionCategory }))}
                >
                  {CATEGORIES.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      {c.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Priority"
                type="number"
                value={formData.priority}
                onChange={(e) => setFormData((p) => ({ ...p, priority: parseInt(e.target.value) || 0 }))}
                sx={{ width: 140 }}
              />
            </Stack>
            <TextField
              label="Summary"
              fullWidth
              helperText="Brief one-liner for quick agent responses"
              value={formData.summary}
              onChange={(e) => setFormData((p) => ({ ...p, summary: e.target.value }))}
            />
            <TextField
              label="Content (Markdown)"
              required
              fullWidth
              multiline
              minRows={8}
              maxRows={20}
              value={formData.content}
              onChange={(e) => setFormData((p) => ({ ...p, content: e.target.value }))}
              helperText="Full instruction text in Markdown format."
            />
            {/* Tags */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Tags
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
                {formData.tags.map((tag) => (
                  <Chip key={tag} label={tag} size="small" onDelete={() => removeTag(tag)} />
                ))}
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  placeholder="Add tag…"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                />
                <Button size="small" onClick={addTag}>
                  Add
                </Button>
              </Stack>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData((p) => ({ ...p, is_active: e.target.checked }))}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!formData.title || !formData.service_key || !formData.content || createMutation.isPending || updateMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending ? (
              <CircularProgress size={20} />
            ) : editingSlug ? (
              'Update'
            ) : (
              'Create'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteSlug} onClose={() => setDeleteSlug(null)} maxWidth="xs">
        <DialogTitle>Delete Service Instruction?</DialogTitle>
        <DialogContent>
          <Typography>This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSlug(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteSlug && deleteMutation.mutate(deleteSlug)}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

/**
 * Expanded content row — fetches full detail by slug.
 */
const ExpandedContent: React.FC<{ slug: string; tags: string[] }> = ({ slug, tags }) => {
  const { data, isLoading } = useQuery<ServiceInstruction>({
    queryKey: ['service-instruction-detail', slug],
    queryFn: () => serviceInstructionsApi.get(slug),
  });

  if (isLoading) {
    return (
      <Box py={2} display="flex" justifyContent="center">
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!data) return null;

  return (
    <Box py={2}>
      {tags.length > 0 && (
        <Stack direction="row" spacing={0.5} mb={1} flexWrap="wrap">
          {tags.map((t) => (
            <Chip key={t} label={t} size="small" variant="outlined" />
          ))}
        </Stack>
      )}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          whiteSpace: 'pre-wrap',
          fontFamily: 'monospace',
          fontSize: '0.85rem',
          maxHeight: 400,
          overflow: 'auto',
          bgcolor: 'action.hover',
        }}
      >
        {data.content}
      </Paper>
      <Typography variant="caption" color="text.secondary" mt={1} display="block">
        Last updated: {data.updated_at ? new Date(data.updated_at).toLocaleString() : 'N/A'}
        {data.created_by_username && ` · By: ${data.created_by_username}`}
      </Typography>
    </Box>
  );
};

export default ServiceInstructionsPage;
